import { getDispatch } from '../../../spa/store';
import { getRequest, postRequest } from '../../utils/requestService';
import { API_URL } from '../../constants';
import { TAudio, TUserGroup } from '../../../spa/store/models/types';
import { TConferenceSaveLevel } from './conferenceSaveLevel';

export type TConferenceCreateRoomResult = {
	name: string;
	url: string;
	token: string;
	saveLevel: TConferenceSaveLevel;
	startedAt?: number; // server clock, ms
	serverNow?: number;
}

// result:false + reason:'finished' means the call is over and can't be re-joined.
// owner gets token
export type TConferenceJoinResult = {
	result: boolean;
	reason?: string;
	token?: string;
	finished?: boolean; // owner let into a finished call (still within the reopen window)
	saveLevel?: TConferenceSaveLevel;
	startedAt?: number; // server clock, ms
	serverNow?: number;
};

export type TConferenceProcessResult = TUserGroup & { error?: string };
export type TConferenceReprocessResult = TAudio & { error?: string };
export type TConferenceSettingsResult = TConferenceProcessResult;

// cancelling:true — abort signal sent; false — nothing to cancel. Rollback runs in the background.
export type TConferenceCancelResult = {
	cancelling?: boolean;
	error?: string;
};

// 200 with an empty body means deleted; a non-200 carries { error } in the body.
export type TConferenceDeleteResult = {
	error?: string;
};

// Which stages a reprocess run should redo. Both default to true; `transcribe: false` keeps the
// existing captions (the audio is still rebuilt — the video build needs it), `video: false` keeps
// the existing video file.
export type TConferenceProcessSteps = {
	transcribe: boolean;
	video: boolean;
};

// Defined in its own import-free module so the store's type modules can use it without a cycle;
// re-exported here since this is where the rest of the conference REST types live.
export type { TConferenceSaveLevel };

// A partial patch of conference settings; only the changed fields are sent.
export type TConferenceSettings = {
	langId?: number;
	multilingual?: boolean;
	transcriptionProvider?: string | null;
	saveLevel?: TConferenceSaveLevel;
};

export type TConferenceCompareItem = {
	provider: string;
	captionsCount: number;
	ok: boolean;
	error?: string;
};
export type TConferenceComparePhase = 'RUNNING' | 'DONE' | 'FAILED';
export type TConferenceCompareProgress = {
	phase: TConferenceComparePhase;
	total: number;
	completed: number;
	current?: string;
	audioId: number;
	results: TConferenceCompareItem[];
};
// compare() is async: it returns at once and runs providers in the background.
// Poll compareProgress() until phase is DONE/FAILED.
export type TConferenceCompareResult = {
	started: boolean;
	audioId: number;
	progress: TConferenceCompareProgress;
};

export class ConferenceRest {

	private static CREATE_ROOM_URL = API_URL + '/conference/createRoom';
	private static JOIN_ROOM_URL = API_URL + '/conference/joinRoom';
	private static GET_ROOM_TOKEN_URL = API_URL + '/conference/roomToken';
	private static PROCESS_URL = API_URL + '/conference/process';
	private static REPROCESS_URL = API_URL + '/conference/reprocess';
	private static CANCEL_URL = API_URL + '/conference/cancel';
	private static SETTINGS_URL = API_URL + '/conference/settings';
	private static COMPARE_URL = API_URL + '/conference/compare';
	private static SET_USER_SERVICE_ID_URL = API_URL + '/conference/setUserServiceId';
	private static DELETE_URL = API_URL + '/conference/delete';

	public static async createRoom(
		lang: string,
		multilingual?: boolean,
	): Promise<TConferenceCreateRoomResult> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.CREATE_ROOM_URL, { lang, multilingual }));
	}

	public static async joinRoom(roomName: string): Promise<TConferenceJoinResult> {
		const dispatch = getDispatch();
		const resp = await dispatch(postRequest(this.JOIN_ROOM_URL, { roomName }));
		return {
			result: !!resp?.result,
			reason: resp?.reason,
			token: resp?.token,
			finished: !!resp?.finished,
			saveLevel: resp?.saveLevel,
			startedAt: resp?.startedAt,
			serverNow: resp?.serverNow,
		};
	}

	public static async getRoomToken(roomName: string, owner: boolean): Promise<string> {
		const dispatch = getDispatch();
		const result = await dispatch(postRequest(this.GET_ROOM_TOKEN_URL, { roomName, owner }));
		return result?.token || '';
	}

	// saveLevel: the caller's own live value at the moment of calling (see conferenceProcessAction's
	// TBody.saveLevel) — closes the race against /settings' own async persist of the same field.
	public static async process(roomName: string, saveLevel?: TConferenceSaveLevel): Promise<TConferenceProcessResult | undefined> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.PROCESS_URL, { roomName, saveLevel }));
	}

	// `steps` picks what to redo; omitted flags default to true on the backend.
	public static async reprocess(
		roomName: string,
		force?: boolean,
		steps?: TConferenceProcessSteps
	): Promise<TConferenceReprocessResult | undefined> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.REPROCESS_URL, {
			roomName,
			...(force ? { force: true } : {}),
			...(steps?.transcribe === false ? { transcribe: false } : {}),
			...(steps?.video === false ? { video: false } : {})
		}));
	}

	// Signal an in-flight process/reprocess run to abort. Returns instantly; the poll surfaces the result.
	public static async cancel(roomName: string): Promise<TConferenceCancelResult | undefined> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.CANCEL_URL, { roomName }));
	}

	// Persist a partial patch of conference settings for a room
	// Returns the conference group the audio now belongs to (it may move between language groups).
	public static async setSettings(roomName: string, settings: TConferenceSettings): Promise<TConferenceSettingsResult | undefined> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.SETTINGS_URL, { roomName, ...settings }));
	}

	public static async compare(
		roomName: string,
		providers: string[],
	): Promise<TConferenceCompareResult | undefined> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.COMPARE_URL, { roomName, providers }));
	}

	// Poll the compare run's progress. `progress: null` — no run (or TTL expired ~60s after finish).
	public static async compareProgress(roomName: string): Promise<{ progress: TConferenceCompareProgress | null } | undefined> {
		const dispatch = getDispatch();
		return await dispatch(getRequest(`${this.COMPARE_URL}/${roomName}`));
	}

	public static async setUserServiceId(roomName: string, serviceUserId: string): Promise<any> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.SET_USER_SERVICE_ID_URL, { roomName, serviceUserId }));
	}

	// Delete the conference recording. Resolves on 200 (empty body); rejects with { error } on failure.
	public static async delete(roomName: string): Promise<TConferenceDeleteResult | undefined> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(this.DELETE_URL, { roomName }));
	}

}
